/**
 * One member's picks, round by round, in the shared overlay (ISSUE-1318).
 *
 * Opened from a standings row or from a member's pool profile. It is the web
 * twin of the legacy player sheet and of mobile's pick history screen: the
 * member's status, their numbers, the teams they have used, then every locked
 * pick with its result. Bottom sheet under 768px, centered dialog above, from
 * the one `Dialog` in `@sportsmet/ui` (`responsive-overlays.mdc`).
 *
 * The read is `POST /results/get-by-member`, which serves any member of the
 * pool and only ever returns locked picks. It runs only while the sheet is
 * open, and never caches, because grading changes it and nothing in this app
 * can invalidate it.
 */
import { useResultsGetByMember } from "@sportsmet/contest-sdk/private";
import { Button, Dialog, InlineBanner, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { useTeamNames } from "../pick/useTeamNames";
import {
  buildMemberRoundsView,
  MEMBER_ROUNDS_CLOSE_LABEL,
  MEMBER_ROUNDS_DESCRIPTION,
  MEMBER_ROUNDS_FAILED_MESSAGE,
  MEMBER_ROUNDS_LOADING_LABEL,
  MEMBER_ROUNDS_PICKS_TITLE,
  MEMBER_ROUNDS_RETRY_LABEL,
  MEMBER_ROUNDS_TEAMS_USED_TITLE,
  type typeMemberRoundsView,
} from "./memberRoundsModel";
import { statusLabelFor } from "./poolStandingsModel";
import { SurvivorRoundList } from "./SurvivorRoundList";

export type typeMemberRoundsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  poolId: string;
  contestId: string;
  targetUserId: string;
  displayName: string;
  /** The standings row it was opened from, when there was one. */
  focusEntryId?: string | null | undefined;
  wins?: number | null | undefined;
  losses?: number | null | undefined;
};

export const MemberRoundsSheet = (
  props: typeMemberRoundsSheetProps,
): ReactNode => {
  const isReady =
    props.open &&
    props.poolId !== "" &&
    props.contestId !== "" &&
    props.targetUserId !== "";
  const rounds = useResultsGetByMember(
    {
      poolId: props.poolId,
      contestId: props.contestId,
      targetUserId: props.targetUserId,
    },
    { enabled: isReady, staleTime: 0, refetchOnMount: "always" },
  );
  const isFailed = rounds.isError || extractErrorCode(rounds.data) !== null;
  const wireEntries = rounds.data?.data?.entries;

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={props.displayName}
      description={MEMBER_ROUNDS_DESCRIPTION}
      autoFocus={false}
      footer={
        <Button
          variant="secondary"
          onClick={() => {
            props.onOpenChange(false);
          }}
          data-testid="member-rounds-close"
        >
          {MEMBER_ROUNDS_CLOSE_LABEL}
        </Button>
      }
    >
      <div className="flex flex-col gap-4" data-testid="member-rounds-sheet">
        {isFailed ? (
          <div data-testid="member-rounds-failed">
            <InlineBanner
              variant="warning"
              message={MEMBER_ROUNDS_FAILED_MESSAGE}
              action={{
                label: MEMBER_ROUNDS_RETRY_LABEL,
                onAction: () => {
                  void rounds.refetch();
                },
              }}
            />
          </div>
        ) : wireEntries === undefined ? (
          <div data-testid="member-rounds-loading">
            <Skeleton shape="card" label={MEMBER_ROUNDS_LOADING_LABEL} />
          </div>
        ) : (
          <MemberRoundsBody
            poolId={props.poolId}
            view={buildMemberRoundsView({
              entries: wireEntries,
              focusEntryId: props.focusEntryId ?? null,
              wins: props.wins,
              losses: props.losses,
            })}
          />
        )}
      </div>
    </Dialog>
  );
};

/**
 * Mounts only once the rounds have arrived, because `useTeamNames` has no
 * `enabled` switch. While the names load the skeleton stays, so a fan never
 * sees a team id turn into a name.
 */
function MemberRoundsBody(props: {
  poolId: string;
  view: typeMemberRoundsView;
}): ReactNode {
  const teamNames = useTeamNames({ poolId: props.poolId });
  if (teamNames.isLoading) {
    return (
      <div data-testid="member-rounds-loading">
        <Skeleton shape="card" label={MEMBER_ROUNDS_LOADING_LABEL} />
      </div>
    );
  }
  const { view } = props;
  return (
    <>
      {view.status === null ? null : (
        <span
          className="w-fit rounded-full border border-hairline px-2 py-1 text-caption text-ink"
          data-testid="member-rounds-status"
        >
          {statusLabelFor(view.status)}
        </span>
      )}
      <dl className="grid grid-cols-4 gap-2" data-testid="member-rounds-stats">
        {view.stats.map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col items-center gap-1 rounded-lg bg-surface-sunken px-2 py-3"
          >
            <dt className="text-caption text-ink-secondary">{stat.label}</dt>
            <dd
              className="text-h2 tabular-nums text-ink"
              data-testid={`member-rounds-stat-${stat.label.toLowerCase()}`}
            >
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
      {view.usedTeamIds.length === 0 ? null : (
        <section
          className="flex flex-col gap-2"
          data-testid="member-rounds-teams-used"
        >
          <h3 className="text-body text-ink">
            {MEMBER_ROUNDS_TEAMS_USED_TITLE}
          </h3>
          <ul className="flex flex-wrap gap-2">
            {view.usedTeamIds.map((teamId) => {
              const team = teamNames.teamsById.get(teamId);
              return (
                <li
                  key={teamId}
                  className="flex items-center gap-1 rounded-full bg-surface-sunken px-2 py-1 text-caption text-ink"
                  data-testid={`member-rounds-team-${teamId}`}
                >
                  {team?.logoUrl ? (
                    <img
                      src={team.logoUrl}
                      alt=""
                      className="size-4 shrink-0 object-contain"
                    />
                  ) : null}
                  {team === undefined || team.abbreviation === ""
                    ? (team?.name ?? teamId)
                    : team.abbreviation}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <section className="flex flex-col gap-2">
        <h3 className="text-body text-ink">{MEMBER_ROUNDS_PICKS_TITLE}</h3>
        <SurvivorRoundList
          entries={view.entries}
          teamsById={teamNames.teamsById}
        />
      </section>
    </>
  );
}
